const TURNSTILE_SCRIPT_ID = 'flow-wizard-turnstile-script';
const TURNSTILE_SCRIPT_SOURCE =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileRenderOptions {
  action: 'contact_submit';
  callback: (token: string) => void;
  'error-callback': () => void;
  'expired-callback': () => void;
  sitekey: string;
}

export interface TurnstileApi {
  remove(widgetId: string): void;
  render(
    container: HTMLElement,
    options: TurnstileRenderOptions,
  ): string;
  reset(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type TurnstileLoader = () => Promise<TurnstileApi>;

let turnstilePromise: Promise<TurnstileApi> | null = null;

export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile !== undefined) return Promise.resolve(window.turnstile);
  if (turnstilePromise !== null) return turnstilePromise;

  const pending = new Promise<TurnstileApi>((resolve, reject) => {
    const rejectLoad = (script: HTMLScriptElement) => {
      script.dataset.turnstileState = 'failed';
      script.remove();
      reject(new Error('Turnstile could not be loaded.'));
    };
    const resolveLoadedApi = (script: HTMLScriptElement) => {
      script.dataset.turnstileState = 'loaded';
      if (window.turnstile === undefined) {
        rejectLoad(script);
        return;
      }
      resolve(window.turnstile);
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `#${TURNSTILE_SCRIPT_ID}`,
    );
    if (existing !== null) {
      if (
        existing.dataset.turnstileState === 'loaded' ||
        existing.dataset.turnstileState === 'failed'
      ) {
        rejectLoad(existing);
        return;
      }
      existing.addEventListener('load', () => resolveLoadedApi(existing), {
        once: true,
      });
      existing.addEventListener('error', () => rejectLoad(existing), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SOURCE;
    script.async = true;
    script.defer = true;
    const bootstrapNonce =
      document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;
    if (bootstrapNonce !== undefined && bootstrapNonce.length > 0) {
      script.nonce = bootstrapNonce;
    }
    script.dataset.turnstileState = 'loading';
    script.addEventListener('load', () => resolveLoadedApi(script), {
      once: true,
    });
    script.addEventListener('error', () => rejectLoad(script), { once: true });
    document.head.append(script);
  });
  turnstilePromise = pending.catch((error: unknown) => {
    turnstilePromise = null;
    throw error;
  });
  return turnstilePromise;
}
